<template>
  <div class="mt-2">
    <div class="py-5">
      <div class="title">{{$t('text_around_qr_title')}}</div>
      <div class="description">
        {{$t('text_around_qr_hint')}}
        <a
          :href="textAroundQrLearnMoreLink"
          @click.prevent="openHelpCenterArticle(textAroundQrLearnMoreLink)"
          target="_blank"
          class="text-decoration-underline"
          >
          {{ $t('learn_more') }}
        </a>
      </div>

      <div :key="`renderKey-${renderKey}`">
        <v-row class="ma-0 mt-5 my-2" v-for="(item) in filteredTexts" :key="`textfield-${item.key}`">
          <v-row class="text-row px-1 ma-0">
            <v-col cols="3" class="pa-0 mr-2">
              <v-select
                solo
                class="text-select"
                :value="item.key"
                :items="textPositions"
                :label="$t('position')"
                item-text="description"
                item-value="key"
                hide-details
                dense
                @change="onTextPositionChange($event, item.key)"
              >
              </v-select>
            </v-col>
            <v-col class="pa-0 mr-2">
              <v-combobox @input="handleTextInput($event, item.key)" maxlength="33" class="ma-0" solo hide-details dense :items="dataFields" v-model="textSettings.selectedTexts[item.key]" item-text="description" return-object></v-combobox>
            </v-col>
            <v-col cols="1" class="pa-0">
              <v-btn color="red" @click="deleteTextField(item.key)" icon><v-icon>mdi-close-circle</v-icon></v-btn>
            </v-col>
          </v-row>
        </v-row>
        <v-row v-if="Object.keys(textSettings.selectedTexts).length > 0" class="pa-0 ma-0">
          <v-col cols="3" class="pa-0 mr-2"></v-col>
          <v-col class="pa-0 mr-2">
            <div class="mt-1 ml-1 grey--text text--darken-1">{{$t('text_around_qr_line_break_hint')}}</div>
          </v-col>
          <v-col cols="1" class="pa-0"></v-col>
        </v-row>
      </div>
      <v-btn class="mt-3" text color="primary" v-if="hasAvailableTextPosition" @click="addTextField()">
        <v-icon>$plus</v-icon>
        {{$t('add_data')}}
      </v-btn>
    </div>

    <v-row v-if="filteredTexts.length" class="mt-2 mb-8">
      <v-col cols="12" md="4" class="pt-0 pb-0 mb-4">
        <div class="label font-weight-bold pb-1">{{$t('text_color')}}:</div>
        <color-input class="w-100 max-w-170" :color.sync="textSettings.text.color" />
      </v-col>

      <v-col cols="12" md="8" class="pb-0 mb-1">
        <v-row>
          <v-col cols="12" md="6" class="pt-0 pb-0">
            <div class="label font-weight-bold pb-1">{{$t('text_size')}}:</div>
            <v-select
              v-model="textScaleProxy"
              :items="textScaleOptions"
              hide-details
              solo
              class="w-100 max-w-170"
            >
              <template v-slot:selection="{ item }">
                {{ $t(item) }}
              </template>

              <template v-slot:item="{ item }">
                {{ $t(item) }}
              </template>
            </v-select>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <div class="mt-1 mb-6">
      <div class="label font-weight-bold pb-1">{{$t('qr_size')}}:</div>
      <div class="hint">{{$t('qr_size_hint')}} <a :href="qrcodeSizeLearnMoreLink" @click.prevent="openHelpCenterArticle(qrcodeSizeLearnMoreLink)" target='_blank' class="text-decoration-underline">{{$t('learn_more')}}</a></div>

      <v-slider
        class="pt-2 px-2"
        v-model="qrScaleProxy"
        max="4"
        min="0"
        :step="1"
        ticks
        dense
        hide-details
      ></v-slider>
      <v-row class="ma-0" justify="space-between">
        <div class="size-hint font-weight-bold">
          {{$t('small')}}
        </div>
        <div class="size-hint font-weight-bold">
          {{$t('medium')}}
        </div>
        <div class="size-hint font-weight-bold">
          {{$t('large')}}
        </div>
      </v-row>
    </div>

    <div>
      <div class="title">{{$t('icon_options_title')}}</div>
      <div class="description">{{$t('icon_options_hint')}}</div>
      <v-radio-group hide-details v-model="textSettings.iconSetting" class="pa-2">
        <v-radio value="none" :label="$t('no_icon')"></v-radio>
        <v-radio value="inside" :label="$t('icon_inside_of_qr')"></v-radio>
        <v-radio value="outside" :label="$t('icon_outside_of_qr')"></v-radio>
      </v-radio-group>

      <div v-if="textSettings.iconSetting === 'outside'" class="outside-icon-options mt-2 ml-7 mb-5">
        <v-select
          solo
          class="text-select"
          v-model="iconPosition"
          :items="textPositions"
          :label="$t('position')"
          item-text="description"
          item-value="key"
          hide-details
          dense
        >
        </v-select>
      </div>
    </div>
  </div>
</template>

<script>
import Utils from '@/utils/utils'
import { openHelpCenterArticle } from '@/utils/helpCenter'
import { mapGetters } from 'vuex'
import ColorInput from '@/components/utils/ColorInput'

export default {
  name: 'TextAroundQrCodeSettings',
  components: {
    ColorInput
  },
  props: ['codes', 'product', 'elabelData', 'selectedTemplate', 'textPositions', 'qrScale', 'textScale'],
  data () {
    return {
      textSettings: {
        text: {},
        selectedTexts: {},
        iconSetting: 'none'
      },
      qrScaleInput: 2,
      textScaleInput: 2,
      kj: 0,
      kcal: 0,
      iconPosition: '',
      renderKey: 1
    }
  },
  watch: {
    selectedTemplate (newVal) {
      if (newVal) {
        const template = JSON.parse(JSON.stringify(newVal))
        this.iconPosition = ''
        this.textSettings.iconSetting = template.image ? 'inside' : 'none'
        this.textSettings.selectedTexts = {}
        const text = template.text || {}
        this.textSettings.text = { color: text?.color }

        Object.entries(text).forEach(([key, value]) => {
          const skippedKeys = ['color', 'scale']
          // non-GS1 codes don't have gtin
          if (skippedKeys.includes(key) || (this.hasStandardCode && value === '{gtin}')) {
            return
          }

          // Replaces line breaks with '\'
          const displayValue = value.replace(/\n/, '\\')
          const matchedField = this.dataFields.find((field) => displayValue.includes(field.text))

          if (displayValue) {
            this.$set(this.textSettings.selectedTexts, key, { text: displayValue, description: matchedField?.description ? matchedField.description : displayValue })
          }

          if (displayValue.indexOf('ⓘ ') > -1) {
            this.textSettings.iconSetting = 'outside'
            this.iconPosition = key
          }
        })
      }
    },

    'textSettings.iconSetting' (newVal, oldVal) {
      if (oldVal === 'outside' && newVal !== 'outside') {
        for (const position in this.textSettings.selectedTexts) {
          if (this.textSettings.selectedTexts[position].text.indexOf('ⓘ ') > -1) {
            this.textSettings.selectedTexts[position].text = this.textSettings.selectedTexts[position].text.substr(2)
            if (!this.textSettings.selectedTexts[position].text.length) {
              this.$delete(this.textSettings.selectedTexts, position)
              this.$delete(this.textSettings.text, position)
            }
          }
        }
        this.iconPosition = ''
      }
    },

    iconPosition (newVal, oldVal) {
      if (this.textSettings.iconSetting === 'outside') {
        if (oldVal && this.textSettings.selectedTexts[oldVal]) {
          if (this.textSettings.selectedTexts[oldVal]?.text?.indexOf('ⓘ ') > -1) {
            this.textSettings.selectedTexts[oldVal].text = this.textSettings.selectedTexts[oldVal].text.substr(2)
          }
          if (!this.textSettings.selectedTexts[oldVal].text) {
            this.$delete(this.textSettings.selectedTexts, oldVal)
            this.$delete(this.textSettings.text, oldVal)
          }
        }

        if (!this.textSettings.selectedTexts[newVal] && this.textSettings.selectedTexts[newVal] !== null) {
          this.$set(this.textSettings.selectedTexts, newVal, { description: '', text: '' })
        }

        if (this.textSettings.selectedTexts[newVal].text.indexOf('ⓘ ') === -1) {
          this.textSettings.selectedTexts[newVal].text = `ⓘ ${this.textSettings.selectedTexts[newVal].text ? this.textSettings.selectedTexts[newVal].text : ''}`
        }
      }
    },
    computedSelectedTextsToBeWatched: {
      handler (newVal, oldVal) {
        for (const position in newVal) {
          if (oldVal[position]?.text?.indexOf('ⓘ ') > -1 && newVal[position]?.text?.indexOf('ⓘ ') === -1 && this.iconPosition && this.iconPosition === position) {
            newVal[position].text = `ⓘ ${newVal[position].text}`
          }
          this.$set(this.textSettings.text, position, this.convertToValue(newVal[position]?.text))
        }
      },
      deep: true
    },
    textSettings: {
      handler (newVal) {
        if (newVal) {
          const data = JSON.parse(JSON.stringify(newVal))
          for (const key of Object.keys(data.text)) {
            if (key !== 'color') {
              data.text[key] = data.text[key].replace(/\\/, '\n')
            }
          }
          this.$emit('text-data-updated', data)
        }
      },
      deep: true
    },

    qrScale: {
      handler (newVal) {
        if (newVal && newVal !== this.qrScaleInput) {
          this.qrScaleInput = newVal
        }
      }
    },

    textScale: {
      handler (newVal) {
        if (newVal && newVal !== this.textScaleInput) {
          this.textScaleInput = newVal
        }
      }
    }
  },
  computed: {
    ...mapGetters(['products', 'language']),

    hasStandardCode () {
      return this.codes.some(code => !this.isGS1Code(code.message))
    },

    dataFields () {
      return [
        { key: 'energy', description: this.$t('energy'), text: '{energy}' },
        ...(this.codes.length > 1 || this.codes[0].scm_data.gs1_gtin || this.codes[0].scm_data.gtin ? [{ key: 'gtin', description: this.$t('gtin'), text: '{gtin}' }]
          : []),
        { key: 'sku', description: this.$t('sku'), text: '{sku}' }
      ]
    },

    computedSelectedTextsToBeWatched () {
      return Object.assign({}, this.textSettings.selectedTexts)
    },

    filteredTexts () {
      const array = []
      for (const k in this.textSettings.text) {
        if (k !== 'color') {
          array.push({ key: k, value: this.textSettings.text[k] })
        }
      }
      return array
    },

    hasAvailableTextPosition () {
      return this.textPositions.filter((c) => {
        return !(Object.prototype.hasOwnProperty.call(this.textSettings.text, c.key))
      }).length
    },

    qrScaleProxy: {
      get () {
        if (this.qrScaleInput === 1) return 0
        if (this.qrScaleInput === 2) return 1
        if (this.qrScaleInput === 4) return 2
        if (this.qrScaleInput === 6) return 3
        if (this.qrScaleInput === 10) return 4
        return 1
      },
      set (sliderIndex) {
        this.$emit('qr-scale-updated', sliderIndex)
      }
    },

    textScaleProxy: {
      get () {
        if (this.textScaleInput === 2) return 'small'
        if (this.textScaleInput === 3) return 'medium'
        if (this.textScaleInput === 6) return 'large'
        return 'medium'
      },
      set (size) {
        this.$emit('text-scale-updated', size)
      }
    },

    textScaleOptions () {
      return ['small', 'medium', 'large']
    },

    textAroundQrLearnMoreLink () {
      switch (this.language) {
        case 'de':
          return 'https://help.scantrust.com/hc/de/articles/11085111089180-e-label-Editor-QR-Codes-E-Label-QR-Codes-im-Bulk-herunterladen-Enterprise'
        case 'es':
          return 'https://help.scantrust.com/hc/es-419/articles/11085111089180-e-label-Editor-C%C3%B3digos-QR-C%C3%B3mo-Descargar-C%C3%B3digos-QR-de-e-label-Masivamente'
        case 'fr':
          return 'https://help.scantrust.com/hc/fr/articles/11085111089180-e-label-Editeur-QR-Codes-Comment-t%C3%A9l%C3%A9charger-massivement-les-codes-QR-E-Label'
        case 'it':
          return 'https://help.scantrust.com/hc/it/articles/11085111089180-e-label-Editor-Codices-QR-Come-scaricare-in-modo-massiccio-i-codici-QR-e-label'
        case 'pt':
          return 'https://help.scantrust.com/hc/pt/articles/11085111089180-e-label-Editor-C%C3%B3digos-QR-Download-de-c%C3%B3digos-QR-de-etiqueta-eletr%C3%B4nica-em-massa-Enterprise'
        case 'zh':
          return 'https://help.scantrust.com/hc/zh-cn/articles/11085111089180-e-label-%E7%BC%96%E8%BE%91%E5%99%A8-%E4%BA%8C%E7%BB%B4%E7%A0%81-%E6%89%B9%E9%87%8F%E4%B8%8B%E8%BD%BD%E7%94%B5%E5%AD%90%E6%A0%87%E7%AD%BE%E4%BA%8C%E7%BB%B4%E7%A0%81-Enterprise'
        default:
          return 'https://help.scantrust.com/hc/en-us/articles/11085111089180-e-label-Editor-QR-Codes-Downloading-e-label-QR-codes-in-Bulk-Enterprise'
      }
    },

    qrcodeSizeLearnMoreLink () {
      switch (this.language) {
        case 'de':
          return 'https://help.scantrust.com/hc/de/articles/20680564945436-Text-around-QR-Code-coming-soon'
        case 'es':
          return 'https://help.scantrust.com/hc/es-419/articles/20680564945436-Text-around-QR-Code-coming-soon'
        case 'fr':
          return 'https://help.scantrust.com/hc/fr/articles/20680564945436-Text-around-QR-Code-coming-soon'
        case 'it':
          return 'https://help.scantrust.com/hc/it/articles/20680564945436-Text-around-QR-Code-coming-soon'
        case 'pt':
          return 'https://help.scantrust.com/hc/pt/articles/20680564945436-Text-around-QR-Code-coming-soon'
        case 'zh':
          return 'https://help.scantrust.com/hc/zh-cn/articles/20680564945436-Text-around-QR-Code-coming-soon'
        default:
          return 'https://help.scantrust.com/hc/en-us/articles/20680564945436-Text-around-QR-Code-coming-soon'
      }
    }
  },
  methods: {
    isGS1Code (codeMessage) {
      return Utils.isGS1Identifier(codeMessage)
    },

    forceReRender () {
      // Increment renderKey to force a refresh
      this.renderKey += 1
    },

    openHelpCenterArticle,

    updateIconSetting (value) {
      this.textSettings.iconSetting = value
    },

    handleTextInput (e, key) {
      if (e === null) {
        this.textSettings.selectedTexts[key] = { text: '', description: '' }
      } else if (typeof e === 'string') {
        this.textSettings.selectedTexts[key] = { text: e, description: e }
      } else {
        this.textSettings.selectedTexts[key] = JSON.parse(JSON.stringify(e))
      }
    },

    replaceVariables (string, replacements) {
      if (!string) return ''
      return string.replace(/\{(.*?)\}/g, (match, p1) => replacements[p1] || match)
    },

    convertToValue (string) {
      if (!string) {
        return ''
      }

      const variablesReplacements = {
        energy: `E(100ml)=${this.kj}kJ/${this.kcal}kcal`,
        // eslint-disable-next-line
        gtin: `(01) ${this.codes[0].scm_data.gs1_gtin || this.codes[0].scm_data.gtin}`,
        sku: this.codes[0].product.sku
      }

      return this.replaceVariables(string, variablesReplacements)
    },

    onTextPositionChange (newKey, oldKey) {
      if (newKey) {
        if (this.textSettings.selectedTexts[oldKey]) {
          if (this.textSettings.selectedTexts[oldKey].text.indexOf('ⓘ ') > -1) {
            this.$set(this.textSettings.selectedTexts, newKey, { ...this.textSettings.selectedTexts[oldKey], text: this.textSettings.selectedTexts[oldKey].text.split('ⓘ ')[1] })
          } else {
            this.$set(this.textSettings.selectedTexts, newKey, this.textSettings.selectedTexts[oldKey])
          }
        }
        if (oldKey) {
          if (this.textSettings.selectedTexts[oldKey]?.text?.indexOf('ⓘ ') > -1) {
            this.$set(this.textSettings.selectedTexts, oldKey, { key: '', description: '', text: 'ⓘ ' })
          } else {
            this.$delete(this.textSettings.selectedTexts, oldKey)
            this.$delete(this.textSettings.text, oldKey)
          }
        }
        // Hack to make sure the value of the textPosition select is properly updated
        this.forceReRender()
      }
    },

    addTextField () {
      const availablePositions = this.textPositions.filter((c) => {
        return !(Object.prototype.hasOwnProperty.call(this.textSettings.text, c.key))
      })
      if (availablePositions[0]) {
        this.$set(this.textSettings.text, availablePositions[0].key, '')
        this.$set(this.textSettings.selectedTexts, availablePositions[0].key, { description: '', text: '' })
      }

      this.$forceUpdate()
    },

    deleteTextField (key) {
      if (this.textSettings.iconSetting === 'outside' && this.iconPosition === key) {
        this.textSettings.iconSetting = 'none'
        this.$nextTick(() => {
          this.iconPosition = ''
        })
      }

      this.$delete(this.textSettings.selectedTexts, key)
      this.$delete(this.textSettings.text, key)
      this.$forceUpdate()
    }
  },
  async created () {
    this.$set(this.textSettings.text, 'color', '#000000')
    if (this.elabelData?.data?.published) {
      this.kj = this.elabelData.data.published.nutrition.table[0].per_100ml
      this.kcal = this.elabelData.data.published.nutrition.table[0].children[0].per_100ml
    } else if (this.product) {
      // load Elabel data for 'Codes' (All Qr Codes) page
      const product = this.products.find((product) => {
        return product.id === this.product
      })
      if (product?.elabel?.has_data) {
        const elabelData = await this.$store.dispatch('loadElabel', { id: product.id, sku: product.sku })
        if (elabelData?.data?.published) {
          this.kj = elabelData.data.published.nutrition.table[0].per_100ml
          this.kcal = elabelData.data.published.nutrition.table[0].children[0].per_100ml
        }
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.label
  font-size: 17px
.size-hint
  color: #999999
  font-size: 15px
.max-w-170
  max-width: 170px
</style>
